import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react'

interface NavigationInfo {
  scrollY?: number
  accordionStates?: Record<string, string[]>
}

interface AccordionContext {
  getAccordion: (key: string) => AccordionInstance | null
  activateAccordion: (key: string) => Promise<void>
}

interface AccordionInstance {
  getHeaderElement: () => HTMLElement | null
}

interface ScrollContextType {
  registerAccordionContext: (id: string, context: AccordionContext) => void
  unregisterAccordionContext: (id: string) => void
  registerAccordionState: (contextId: string, activeKeys: string[]) => void
  scrollTo: (
    accordionContextId: string,
    accordionKey: string,
    domElement?: HTMLElement,
  ) => Promise<void>
  scrollToPanel: (
    accordionContextId: string,
    accordionKey: string,
  ) => Promise<void>
  setMainContainer: (container: HTMLElement | null) => void
  isNavigatingBack: boolean
}

interface ScrollProviderProps {
  children: ReactNode
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined)

export const ScrollProvider: React.FC<ScrollProviderProps> = ({ children }) => {
  const [accordionContexts, setAccordionContexts] = useState<
    Map<string, AccordionContext>
  >(new Map())
  const [, setNavigationInfo] = useState<NavigationInfo>(() => {
    return (window.history.state as any)?.navigationInfo || {}
  })
  const [mainContainer, setMainContainer] = useState<HTMLElement | null>(null)
  const [isNavigatingBack, setIsNavigatingBack] = useState<boolean>(false)

  // Track scroll position without causing re-renders
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout
    const handleScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        // Update history state directly without React state
        const currentState = window.history.state || {}
        const newInfo: NavigationInfo = {
          ...(currentState.navigationInfo || {}),
          scrollY: window.scrollY,
        }

        window.history.replaceState(
          { ...currentState, navigationInfo: newInfo },
          '',
        )
      }, 100)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [])

  // Separate effect for handling popstate events
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      setIsNavigatingBack(true)
      const savedInfo = (event.state as any)?.navigationInfo as NavigationInfo

      if (typeof savedInfo?.scrollY === 'number') {
        createTemporaryScrollSpace()
        setTimeout(() => {
          window.scrollTo(0, savedInfo.scrollY!)
        }, 100)
      }

      // Only update React state for accordion states
      if (savedInfo?.accordionStates) {
        setNavigationInfo((prev) => ({
          ...prev,
          accordionStates: savedInfo.accordionStates,
        }))
      }

      setTimeout(() => setIsNavigatingBack(false), 1000)
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
    /* eslint-disable-next-line */
  }, [mainContainer])

  // Function to manage temporary scroll space
  const createTemporaryScrollSpace = () => {
    if (!mainContainer) return

    // Create temporary div
    const tempDiv = document.createElement('div')

    tempDiv.style.height = '3000px'
    tempDiv.style.width = '1px'
    tempDiv.style.position = 'absolute'
    tempDiv.style.opacity = '0'
    tempDiv.style.pointerEvents = 'none'
    tempDiv.id = 'temp-scroll-space'

    mainContainer.appendChild(tempDiv)

    // Remove after animation completes
    setTimeout(() => {
      tempDiv.remove()
    }, 1000)
  }

  // Update history state when accordion states change
  const registerAccordionState = (contextId: string, activeKeys: string[]) => {
    setNavigationInfo((prev) => {
      const newInfo: NavigationInfo = {
        ...prev,
        accordionStates: {
          ...(prev.accordionStates || {}),
          [contextId]: activeKeys,
        },
      }
      // Update history state

      window.history.replaceState(
        { ...window.history.state, navigationInfo: newInfo },
        '',
      )

      return newInfo
    })
  }

  // Simple context registration
  const registerAccordionContext = useCallback(
    (id: string, context: AccordionContext) => {
      setAccordionContexts((prev) => {
        const newMap = new Map(prev)

        newMap.set(id, context)

        return newMap
      })
    },
    [],
  )

  const unregisterAccordionContext = useCallback((id: string) => {
    setAccordionContexts((prev) => {
      const newMap = new Map(prev)

      newMap.delete(id)

      return newMap
    })
  }, [])

  /**
   * Scrolls to a panel and opens it, without scrolling to any specific element inside
   * @param accordionContextId - ID of the accordion container
   * @param accordionKey - ID of the specific panel
   */
  const scrollToPanel = useCallback(
    async (accordionContextId: string, accordionKey: string) => {
      const context = accordionContexts.get(accordionContextId)

      if (!context) {
        console.error(
          `Accordion context ${accordionContextId} not found. Available contexts:`,
          Array.from(accordionContexts.keys()),
        )

        return
      }

      const accordion = context.getAccordion(accordionKey)

      if (!accordion) {
        console.error(
          `Accordion ${accordionKey} not found in context ${accordionContextId}`,
        )

        return
      }

      // First scroll to the panel header
      const headerElement = accordion.getHeaderElement()

      if (headerElement) {
        headerElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })

        // Wait for scroll animation to complete before opening
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Open the panel
        await context.activateAccordion(accordionKey)
      }
    },
    [accordionContexts],
  )

  /**
   * Enhanced scroll function that both opens a panel and scrolls to a specific element
   * @param accordionContextId - ID of the accordion container
   * @param accordionKey - ID of the specific panel
   * @param domElement - Specific element to scroll to after opening
   */
  const scrollTo = useCallback(
    async (
      accordionContextId: string,
      accordionKey: string,
      domElement?: HTMLElement,
    ) => {
      // First do the panel scroll
      await scrollToPanel(accordionContextId, accordionKey)

      // Then scroll to the specific element if provided
      if (domElement) {
        // Wait for panel animation to complete
        await new Promise((resolve) => setTimeout(resolve, 300))

        domElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    },
    [scrollToPanel],
  )

  const value: ScrollContextType = {
    registerAccordionContext,
    unregisterAccordionContext,
    registerAccordionState,
    scrollTo,
    scrollToPanel,
    setMainContainer,
    isNavigatingBack,
  }

  return (
    <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
  )
}

export const useScroll = (): ScrollContextType => {
  const context = useContext(ScrollContext)

  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider')
  }

  return context
}
